import React, { useEffect, useState } from "react";
import api, { formatErr } from "../../lib/api";

const cardStyle = { padding: 12, background: "var(--pf-surface)", border: "1px solid var(--pf-border)", borderRadius: 8 };
const labelStyle = { fontSize: 11, fontWeight: 700, color: "var(--pf-muted)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 };
const btnStyle = { padding: "8px 14px", fontSize: 12, fontWeight: 700, background: "var(--pf-ember)", color: "#fff", border: 0, borderRadius: 5, cursor: "pointer" };
const inputStyle = { width: "100%", padding: "6px 8px", fontSize: 12, borderRadius: 5, border: "1px solid var(--pf-border)", background: "var(--pf-surface-2)", color: "var(--pf-fg)" };

export default function DrawingRegisterPanel({ projectId }) {
  const [meta, setMeta] = useState(null);
  const [drawings, setDrawings] = useState([]);
  const [transmittals, setTransmittals] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [filterDisc, setFilterDisc] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [createForm, setCreateForm] = useState({
    drawing_id: "", title: "", discipline: "Process", type: "PFD",
    sheet_size: "A1", scale: "NTS", revision: "0",
  });
  const [err, setErr] = useState(""); const [msg, setMsg] = useState("");

  const load = async () => {
    if (!projectId) return;
    try {
      const params = new URLSearchParams();
      if (filterDisc) params.set("discipline", filterDisc);
      if (filterStatus) params.set("status", filterStatus);
      const [m, d, t, db] = await Promise.all([
        api.get(`/projects/${projectId}/drawings/disciplines`),
        api.get(`/projects/${projectId}/drawings?${params.toString()}`),
        api.get(`/projects/${projectId}/drawings/transmittals`),
        api.get(`/projects/${projectId}/drawings/dashboard`),
      ]);
      setMeta(m.data); setDrawings(d.data); setTransmittals(t.data); setDashboard(db.data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  useEffect(() => { load(); }, [projectId, filterDisc, filterStatus]);

  const create = async () => {
    setErr(""); setMsg("");
    try {
      await api.post(`/projects/${projectId}/drawings`, createForm);
      setMsg(`Created ${createForm.drawing_id}`);
      setCreateForm({ ...createForm, drawing_id: "", title: "" });
      await load();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const transition = async (drawing_id, new_state) => {
    setErr(""); setMsg("");
    try {
      const { data } = await api.post(
        `/projects/${projectId}/drawings/${drawing_id}/transition`,
        { new_state, notes: `Transitioned to ${new_state}` },
      );
      setMsg(data.transmittal ? `→ ${new_state} (transmittal ${data.transmittal.transmittal_id})` : `→ ${new_state}`);
      await load();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const del = async (drawing_id) => {
    if (!window.confirm(`Delete drawing ${drawing_id}?`)) return;
    setErr("");
    try {
      await api.delete(`/projects/${projectId}/drawings/${drawing_id}`);
      await load();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const types = meta?.types_by_discipline?.[createForm.discipline] || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {err && <div style={{ padding: 8, background: "rgba(239,68,68,.1)", color: "#FF8888", borderRadius: 5, fontSize: 12 }}>{err}</div>}
      {msg && <div style={{ padding: 8, background: "rgba(34,197,94,.1)", color: "#22c55e", borderRadius: 5, fontSize: 12 }}>{msg}</div>}

      {dashboard && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
          <KPI label="Total" value={dashboard.total} accent="#F26B1F" />
          {Object.entries(dashboard.by_status).filter(([_, v]) => v > 0).map(([k, v]) =>
            <KPI key={k} label={k.replace("_", " ")} value={v} accent={stateColor(k)} />)}
        </div>
      )}

      <div style={cardStyle}>
        <div style={labelStyle}>Create Drawing</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
          <input style={inputStyle} placeholder="Drawing ID (e.g. P-PFD-001)" data-testid="dr-create-id"
            value={createForm.drawing_id} onChange={(e) => setCreateForm({ ...createForm, drawing_id: e.target.value })} />
          <input style={inputStyle} placeholder="Title" data-testid="dr-create-title"
            value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} />
          <select style={inputStyle} data-testid="dr-create-discipline"
            value={createForm.discipline} onChange={(e) => setCreateForm({ ...createForm, discipline: e.target.value, type: (meta?.types_by_discipline?.[e.target.value] || ["PFD"])[0] })}>
            {(meta?.disciplines || []).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select style={inputStyle} data-testid="dr-create-type"
            value={createForm.type} onChange={(e) => setCreateForm({ ...createForm, type: e.target.value })}>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select style={inputStyle} value={createForm.sheet_size} onChange={(e) => setCreateForm({ ...createForm, sheet_size: e.target.value })}>
            {["A0", "A1", "A2", "A3", "A4"].map((s) => <option key={s}>{s}</option>)}
          </select>
          <input style={inputStyle} placeholder="Revision" value={createForm.revision} onChange={(e) => setCreateForm({ ...createForm, revision: e.target.value })} />
          <button style={btnStyle} data-testid="dr-create-btn" onClick={create}>Add Drawing</button>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
          <div style={{ ...labelStyle, marginBottom: 0, flex: 1 }}>Drawing Register — {drawings.length}</div>
          <select style={{ ...inputStyle, width: 160 }} value={filterDisc} onChange={(e) => setFilterDisc(e.target.value)}>
            <option value="">All disciplines</option>
            {(meta?.disciplines || []).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select style={{ ...inputStyle, width: 160 }} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All states</option>
            {(meta?.workflow_states || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div style={{ overflow: "auto", maxHeight: 380 }}>
          <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--pf-surface-2)", position: "sticky", top: 0 }}>
                {["Drawing ID", "Title", "Disc.", "Type", "Rev.", "Status", "Actions"].map((h) => <th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {drawings.map((d) => {
                const allowed = meta?.allowed_transitions?.[d.current_status] || [];
                return (
                  <tr key={d.id} data-testid={`dr-row-${d.drawing_id}`} style={{ borderBottom: "1px solid var(--pf-border)" }}>
                    <td style={td}><strong style={{ color: "#F26B1F" }}>{d.drawing_id}</strong></td>
                    <td style={td}>{d.title}</td>
                    <td style={td}>{d.discipline}</td>
                    <td style={td}>{d.type}</td>
                    <td style={td}>{d.revision}</td>
                    <td style={td}><StatusPill status={d.current_status} /></td>
                    <td style={td}>
                      {allowed.map((s) =>
                        <button key={s} data-testid={`dr-trans-${d.drawing_id}-${s}`}
                          onClick={() => transition(d.drawing_id, s)}
                          style={{ marginRight: 4, padding: "2px 6px", fontSize: 10,
                                   background: stateColor(s) + "22", color: stateColor(s),
                                   border: `1px solid ${stateColor(s)}55`, borderRadius: 3, cursor: "pointer" }}>
                          → {s}
                        </button>)}
                      <button onClick={() => del(d.drawing_id)} style={{ padding: "2px 6px", fontSize: 10, background: "transparent", color: "#ef4444", border: "1px solid var(--pf-border)", borderRadius: 3, cursor: "pointer" }}>×</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {transmittals.length > 0 && (
        <div style={cardStyle}>
          <div style={labelStyle}>Recent Transmittals</div>
          <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--pf-surface-2)" }}>
                {["ID", "Drawing", "Rev.", "Issued", "Date"].map((h) => <th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {transmittals.slice(0, 20).map((t, i) => (
                <tr key={i} style={{ borderBottom: "1px solid var(--pf-border)" }}>
                  <td style={td}>{t.transmittal_id}</td>
                  <td style={td}>{t.drawing_id}</td>
                  <td style={td}>{t.revision}</td>
                  <td style={td}><StatusPill status={t.issued_state} /></td>
                  <td style={td}>{new Date(t.issued_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th = { padding: 6, textAlign: "left", color: "var(--pf-muted)", textTransform: "uppercase", fontSize: 9 };
const td = { padding: 6, fontFamily: "monospace" };

function stateColor(s) {
  return ({
    DRAFT: "#888", IFR: "#3aa0ff", IFA: "#f59e0b", APPROVED: "#84cc16",
    IFC: "#22c55e", AS_BUILT: "#22c55e", VOID: "#ef4444",
  })[s] || "#888";
}
function StatusPill({ status }) {
  const c = stateColor(status);
  return <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 3, background: c + "22", color: c, border: `1px solid ${c}55`, fontWeight: 700 }}>{status}</span>;
}
function KPI({ label, value, accent = "#F26B1F" }) {
  return (
    <div style={{ padding: 10, background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)", borderRadius: 6, borderLeft: `3px solid ${accent}` }}>
      <div style={{ fontSize: 9, color: "var(--pf-muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  );
}
